import React, { useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTenant, formatCurrencyAmount } from '../../../theme/tokens';
import { getLocaleProfile } from '../../../shared/config/localeProfile';
import { BRAND } from '../../../shared/config/brand';
import { FormField } from '../../../shared/ui/FormField';
import { MetadataEditor, MetadataItem } from '../../../shared/ui/MetadataEditor';
import { CaptureFieldsEditor, CaptureField } from '../../../shared/ui/CaptureFieldsEditor';
import { SettingsIcon, BuildingIcon, PaletteIcon, CreditCardIcon, UsersIcon, ScrollTextIcon, BellIcon, ShieldIcon, FormInputIcon, PercentIcon, ShoppingBagIcon, PackageIcon, InfoIcon, TagIcon, DollarSignIcon, ClockIcon, UploadIcon, PlusIcon, TrashIcon, MailIcon, CheckCircleIcon, WalletIcon, LockIcon, AlertCircleIcon, FileTextIcon, MoreHorizontalIcon, ActivityIcon, SearchIcon, DownloadIcon, RefreshCwIcon, ChevronDownIcon, ChevronUpIcon, ClipboardListIcon } from 'lucide-react';
type SettingsTab = 'company' | 'branding' | 'users' | 'payout' | 'general' | 'payment-providers' | 'receipt' | 'fields' | 'notifications' | 'security' | 'subscription-plan';
interface TabConfig {
  id: SettingsTab;
  label: string;
  icon: any;
  category: 'account' | 'payment' | 'ecommerce' | 'system';
}
interface ActivityLog {
  id: string;
  timestamp: string;
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
  action: string;
  section: string;
  description: string;
  objectId: string;
  details: {
    amount?: string;
  };
  ipAddress: string;
  userAgent: string;
  status: 'success' | 'warning' | 'error' | 'info';
}
export function Settings() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<SettingsTab>('company');
  const currency = getTenant().currency.code;
  // ---- State ----
  const [companyInfo, setCompanyInfo] = useState({
    merchantId: 'MBNK-MY-001234',
    businessName: 'Bila Bila Mart',
    registrationNumber: '202301234567',
    gstNumber: 'MY123456789',
    businessEmail: 'billing@bilabilamart.my',
    businessPhone: '+60 3-1234 5678',
    language: 'en-US'
  });
  const [brandingSettings, setBrandingSettings] = useState({
    logoUrl: BRAND.logoUrl,
    primaryColor: BRAND.colors.primary,
    accentColor: '#2563eb'
  });
  const [generalSettings, setGeneralSettings] = useState({
    defaultExpiryDays: 7,
    redirectUrl: 'https://bilabilamart.my/payment-success',
    enableAutoReminders: true
  });
  const [receiptSettings, setReceiptSettings] = useState({
    showCompanyAddress: true,
    companyAddress: 'Lot 12, Jalan Sultan Ismail\nKuala Lumpur 50250\nMalaysia',
    ccEmail: 'receipts@bilabilamart.my',
    footerText: 'Thank you for your business!'
  });
  const [paymentMethods, setPaymentMethods] = useState([{
    id: 'visa',
    name: 'Visa',
    enabled: true,
    icon: CreditCardIcon,
    category: 'card'
  }, {
    id: 'mastercard',
    name: 'Mastercard',
    enabled: true,
    icon: CreditCardIcon,
    category: 'card'
  }, {
    id: 'amex',
    name: 'American Express',
    enabled: true,
    icon: CreditCardIcon,
    category: 'card'
  }, {
    id: 'fpx',
    name: 'FPX (Online Banking)',
    enabled: true,
    icon: BuildingIcon,
    category: 'banking'
  }, {
    id: 'tng',
    name: "Touch 'n Go eWallet",
    enabled: true,
    icon: WalletIcon,
    category: 'ewallet'
  }, {
    id: 'grabpay',
    name: 'GrabPay',
    enabled: true,
    icon: WalletIcon,
    category: 'ewallet'
  }, {
    id: 'duitnow',
    name: 'DuitNow QR',
    enabled: true,
    icon: WalletIcon,
    category: 'ewallet'
  }, {
    id: 'boost',
    name: 'Boost',
    enabled: false,
    icon: WalletIcon,
    category: 'ewallet'
  }, {
    id: 'shopeepay',
    name: 'ShopeePay',
    enabled: false,
    icon: WalletIcon,
    category: 'ewallet'
  }]);
  const [users, setUsers] = useState([{
    id: '1',
    name: 'Ahmed Lee',
    email: 'ahmed@bilabilamart.my',
    role: 'Owner',
    status: 'Active'
  }, {
    id: '2',
    name: 'Aishath Tan',
    email: 'aishath@bilabilamart.my',
    role: 'Finance',
    status: 'Active'
  }, {
    id: '3',
    name: 'Ibrahim Rahman',
    email: 'ibrahim@bilabilamart.my',
    role: 'Operations',
    status: 'Active'
  }]);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [showAllInvitePerms, setShowAllInvitePerms] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('Operations');
  const [showRoleManagementModal, setShowRoleManagementModal] = useState(false);
  const [openUserMenuId, setOpenUserMenuId] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<{
    id: string;
    name: string;
    email: string;
    role: string;
  } | null>(null);
  // Role definitions with permissions
  const rolePermissions = {
    Owner: {
      description: 'Full access to all features and settings',
      permissions: {
        Dashboard: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Payments: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Refunds: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Customers: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Invoices: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Bills: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Balance: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Settings: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Users: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Reports: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        }
      }
    },
    'Merchant Management': {
      description: 'Create/edit/view merchant accounts, KYC, onboarding',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Refunds: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Customers: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Invoices: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Bills: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Balance: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Settings: {
          read: true,
          write: true,
          modify: true,
          insert: false,
          approve: false
        },
        Users: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        }
      }
    },
    'Account Management': {
      description: 'View and manage existing merchant accounts, support actions, no financial controls',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Refunds: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Customers: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Invoices: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Bills: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Balance: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Settings: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Users: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        }
      }
    },
    Sales: {
      description: 'View merchant pipeline, create leads, view commercial terms, no settlement/finance access',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Refunds: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Customers: {
          read: true,
          write: true,
          modify: false,
          insert: true,
          approve: false
        },
        Invoices: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Bills: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Balance: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Settings: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Users: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        }
      }
    },
    Finance: {
      description: 'View settlements, payouts, MDR, invoices, refunds; no merchant onboarding rights',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Refunds: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Customers: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Invoices: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Bills: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Balance: {
          read: true,
          write: true,
          modify: true,
          insert: false,
          approve: true
        },
        Settings: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Users: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: true,
          modify: false,
          insert: false,
          approve: false
        }
      }
    },
    Operations: {
      description: 'Transaction monitoring, dispute handling, reconciliation, basic merchant edits',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Refunds: {
          read: true,
          write: true,
          modify: true,
          insert: false,
          approve: false
        },
        Customers: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Invoices: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Bills: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Balance: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Settings: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Users: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        }
      }
    }
  };
  const [termsSettings, setTermsSettings] = useState({
    termsText: 'By using our payment services, you agree to our terms and conditions.',
    privacyPolicy: 'We collect and process payment information in accordance with Malaysian data protection regulations.'
  });
  const [presetMetadataFields, setPresetMetadataFields] = useState<MetadataItem[]>([{
    key: 'customer_email',
    value: '',
    isPreset: true,
    required: true,
    type: 'email',
    description: "Customer's email address for receipts"
  }, {
    key: 'invoice_number',
    value: '',
    isPreset: true,
    required: false,
    type: 'text',
    description: 'Reference invoice number'
  }, {
    key: 'order_id',
    value: '',
    isPreset: true,
    required: false,
    type: 'text',
    description: 'Internal order ID'
  }]);
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    paymentReceived: true,
    paymentFailed: true,
    refundProcessed: true,
    disputeOpened: true,
    settlementCompleted: true,
    lowBalance: true
  });
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorEnabled: true,
    sessionTimeout: 30,
    ipWhitelist: '',
    apiKeyVisible: false
  });
  const [selectedShop, setSelectedShop] = useState('Beach Shop');
  const [shopSettings, setShopSettings] = useState({
    inventoryTracking: true,
    lowStockThreshold: 5,
    notifyOnLowStock: false,
    allowBackorders: false,
    hideOutOfStock: false,
    enableTaxCalculation: false,
    pricesIncludeTax: false,
    processingFee: 0,
    shippingFee: 0
  });
  const [taxRates, setTaxRates] = useState([{
    id: '1',
    name: 'Standard Rate',
    rate: 6,
    isDefault: true
  }, {
    id: '2',
    name: 'Reduced Rate',
    rate: 3,
    isDefault: false
  }, {
    id: '3',
    name: 'Zero Rate',
    rate: 0,
    isDefault: false
  }]);
  const [customerFields, setCustomerFields] = useState([{
    id: '1',
    name: 'Full Name',
    type: 'Text',
    required: true,
    enabled: true
  }, {
    id: '2',
    name: 'Email',
    type: 'Email',
    required: true,
    enabled: true
  }, {
    id: '3',
    name: 'Phone Number',
    type: 'Phone',
    required: true,
    enabled: true
  }, {
    id: '4',
    name: 'Address',
    type: 'Text',
    required: true,
    enabled: true
  }, {
    id: '5',
    name: 'Additional Notes',
    type: 'Textarea',
    required: false,
    enabled: true
  }]);
  const [orderSettings, setOrderSettings] = useState({
    orderPrefix: 'ORD-',
    startingNumber: 1000
  });
  // Payout settings state
  const [minBalanceEnabled, setMinBalanceEnabled] = useState(false);
  const [minBalanceAmount, setMinBalanceAmount] = useState('');
  const [payoutDescriptor, setPayoutDescriptor] = useState('');
  const [shopFees, setShopFees] = useState([{
    id: '1',
    name: 'Processing Fee',
    type: 'Percentage',
    amount: 2.5,
    condition: 'Applied to all orders'
  }, {
    id: '2',
    name: 'Shipping Fee',
    type: 'Fixed',
    amount: 5.0,
    condition: 'Applied to orders over MYR 100'
  }]);
  const [shopPayoutSettings, setShopPayoutSettings] = useState({
    bankAccount: 'BML - 773000000001',
    frequency: 'Weekly',
    minimumAmount: 1000
  });
  const [shopNotifications, setShopNotifications] = useState({
    confirmationMessage: 'Thank you for your order! We will process it shortly.',
    notificationEmail: 'notifications@bilabilamart.my',
    notificationPhone: '+60 3-1234 5678',
    sendOrderNotifications: true,
    sendLowStockAlerts: false
  });
  // Activity Logs state
  const [activityLogs] = useState(() => {
    const logs = [];
    const now = new Date();
    const actions = [{
      type: 'created',
      section: 'Payments',
      desc: 'Payment created'
    }, {
      type: 'updated',
      section: 'Payments',
      desc: 'Payment updated'
    }, {
      type: 'deleted',
      section: 'Payments',
      desc: 'Payment deleted'
    }, {
      type: 'created',
      section: 'Customers',
      desc: 'Customer created'
    }, {
      type: 'updated',
      section: 'Customers',
      desc: 'Customer updated'
    }, {
      type: 'deleted',
      section: 'Customers',
      desc: 'Customer deleted'
    }, {
      type: 'created',
      section: 'Invoices',
      desc: 'Invoice created'
    }, {
      type: 'updated',
      section: 'Invoices',
      desc: 'Invoice updated'
    }, {
      type: 'approved',
      section: 'Bills',
      desc: 'Bill approved'
    }, {
      type: 'created',
      section: 'Refunds',
      desc: 'Refund created'
    }, {
      type: 'updated',
      section: 'Settings',
      desc: 'Settings updated'
    }, {
      type: 'created',
      section: 'Users',
      desc: 'User invited'
    }, {
      type: 'updated',
      section: 'Security',
      desc: 'Security settings updated'
    }];
    const realUsers = [{
      id: '1',
      name: 'Ahmed Lee',
      email: 'ahmed@bilabilamart.my',
      role: 'Owner'
    }, {
      id: '2',
      name: 'Aishath Tan',
      email: 'aishath@bilabilamart.my',
      role: 'Finance'
    }, {
      id: '3',
      name: 'Ibrahim Rahman',
      email: 'ibrahim@bilabilamart.my',
      role: 'Operations'
    }];
    for (let i = 0; i < 80; i++) {
      const action = actions[Math.floor(Math.random() * actions.length)];
      const user = realUsers[Math.floor(Math.random() * realUsers.length)];
      const minutesAgo = Math.floor(Math.random() * 43200); // 30 days in minutes
      const timestamp = new Date(now.getTime() - minutesAgo * 60000);
      logs.push({
        id: `log-${i + 1}`,
        timestamp: timestamp.toISOString(),
        user: {
          id: user.id,
          name: user.name,
          email: user.email,
          role: user.role
        },
        action: action.type,
        section: action.section,
        description: action.desc,
        objectId: `${action.section.toLowerCase()}_${Math.random().toString(36).substr(2, 9)}`,
        details: {
          amount: Math.random() > 0.5 ? `MYR ${(Math.random() * 1000).toFixed(2)}` : undefined
        },
        ipAddress: `${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
        userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/58.0.3029.110 Safari/537.3.'
      });
    }
    return logs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  });
  const [logFilters, setLogFilters] = useState({
    search: '',
    dateRange: '7days',
    user: 'all',
    actionType: 'all',
    section: 'all'
  });
  const [logsPerPage, setLogsPerPage] = useState(25);
  const [currentLogPage, setCurrentLogPage] = useState(1);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  // Merchant Requests state
  const [merchantRequests] = useState(() => {
    const now = new Date();
    return [{
      id: 'REQ-001',
      type: 'Payment Method Activation',
      description: 'Request to activate ShopeePay payment method',
      requestedBy: 'Ahmed Lee',
      requestedAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Pending',
      details: {
        paymentMethod: 'ShopeePay',
        reason: 'Customer demand for ShopeePay payments'
      }
    }, {
      id: 'REQ-002',
      type: 'User Role Request',
      description: 'Request for custom "Customer Support" role',
      requestedBy: 'Aishath Tan',
      requestedAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Closed',
      details: {
        roleName: 'Customer Support',
        permissions: 'Read-only access to payments and customers'
      }
    }, {
      id: 'REQ-003',
      type: 'Payment Method Activation',
      description: 'Request to activate Boost eWallet',
      requestedBy: 'Ibrahim Rahman',
      requestedAt: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Closed',
      details: {
        paymentMethod: 'Boost',
        reason: 'Expand payment options for customers',
        rejectionReason: 'Merchant category not supported by Boost'
      }
    }, {
      id: 'REQ-004',
      type: 'Payment Method Activation',
      description: 'Request to activate American Express',
      requestedBy: 'Ahmed Lee',
      requestedAt: new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'In Progress',
      details: {
        paymentMethod: 'American Express',
        reason: 'Corporate clients prefer Amex'
      }
    }, {
      id: 'REQ-005',
      type: 'User Role Request',
      description: 'Request for "Marketing" role with limited access',
      requestedBy: 'Aishath Tan',
      requestedAt: new Date(now.getTime() - 12 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Pending',
      details: {
        roleName: 'Marketing',
        permissions: 'Read access to reports and customer data'
      }
    }, {
      id: 'REQ-006',
      type: 'Payment Method Activation',
      description: 'Request to activate DuitNow QR',
      requestedBy: 'Ibrahim Rahman',
      requestedAt: new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Closed',
      details: {
        paymentMethod: 'DuitNow QR',
        reason: 'Low-cost payment option for customers'
      }
    }].sort((a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime());
  });
  const tabs: TabConfig[] = [
  // ACCOUNT
  {
    id: 'company',
    label: 'Company',
    icon: BuildingIcon,
    category: 'account'
  }, {
    id: 'branding',
    label: 'Branding',
    icon: PaletteIcon,
    category: 'account'
  }, {
    id: 'users',
    label: 'Manage Users',
    icon: UsersIcon,
    category: 'account'
  }, {
    id: 'payout',
    label: 'Payout',
    icon: DollarSignIcon,
    category: 'account'
  },
  // PAYMENT SETTINGS
  {
    id: 'general',
    label: 'General',
    icon: SettingsIcon,
    category: 'payment'
  }, {
    id: 'payment-providers',
    label: 'Payment Providers',
    icon: CreditCardIcon,
    category: 'payment'
  }, {
    id: 'receipt',
    label: 'Receipt Information',
    icon: MailIcon,
    category: 'payment'
  }, {
    id: 'fields',
    label: 'Information Fields',
    icon: FormInputIcon,
    category: 'payment'
  }, {
    id: 'notifications',
    label: 'Notifications',
    icon: BellIcon,
    category: 'payment'
  }, {
    id: 'security',
    label: 'Security',
    icon: ShieldIcon,
    category: 'payment'
  },
  // E-COMMERCE
  {
    id: 'subscription-plan',
    label: 'Subscription Plan',
    icon: CreditCardIcon,
    category: 'account'
  }];
  const categories = [{
    id: 'account',
    label: 'ACCOUNT'
  }, {
    id: 'payment',
    label: 'PAYMENT SETTINGS'
  }];
  const SaveButton = () => <div className="pt-4" data-id="element-10618">
      <button className="px-4 py-2 bg-[var(--brand-primary)] text-[#1A1A1A] font-medium rounded-md hover:bg-[var(--brand-primaryhover)] transition-colors" data-id="element-10619">
        Save Changes
      </button>
    </div>;
  // ---- Render functions ----
  const renderCompanySettings = () => <div className="space-y-6" data-id="element-10620">
      <div data-id="element-10621">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10622">
          Company Information
        </h2>
        <p className="text-sm text-gray-600" data-id="element-10623">
          Manage your business details and merchant account information
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-10624">
        <div className="flex items-start gap-3" data-id="element-10625">
          <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-10626" />
          <div className="text-sm text-gray-700" data-id="element-10627">
            <p className="font-medium text-gray-900 mb-1" data-id="element-10628">
              Verified Merchant Account
            </p>
            <p className="text-xs" data-id="element-10629">
              Your merchant account has been verified and is active. Contact
              support if you need to update your business registration details.
            </p>
          </div>
        </div>
      </div>

      {/* Company Details Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10630">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10631">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10632">
            <BuildingIcon size={18} className="text-gray-600" data-id="element-10633" />
            Business Details
          </h3>
        </div>
        <div className="p-6 space-y-5" data-id="element-10634">
          <FormField label="Merchant ID" tooltip="Your unique merchant identifier" data-id="element-10635">
            <div className="flex items-center gap-2" data-id="element-10636">
              <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50" value={companyInfo.merchantId} readOnly data-id="element-10637" />
              <span className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold bg-green-100 text-green-800 whitespace-nowrap" data-id="element-10638">
                <CheckCircleIcon size={14} className="mr-1" data-id="element-10639" />
                VERIFIED
              </span>
            </div>
          </FormField>

          <FormField label="Business Name" required data-id="element-10640">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[var(--brand-primary)] focus:border-transparent" value={companyInfo.businessName} onChange={e => setCompanyInfo({
            ...companyInfo,
            businessName: e.target.value
          })} data-id="element-10641" />
          </FormField>

          <FormField label="Registration Number" required data-id="element-10642">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[var(--brand-primary)] focus:border-transparent" value={companyInfo.registrationNumber} onChange={e => setCompanyInfo({
            ...companyInfo,
            registrationNumber: e.target.value
          })} data-id="element-10643" />
          </FormField>

          <FormField label={getLocaleProfile().taxIdLabel} data-id="element-10644">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[var(--brand-primary)] focus:border-transparent" value={companyInfo.gstNumber} onChange={e => setCompanyInfo({
            ...companyInfo,
            gstNumber: e.target.value
          })} data-id="element-10645" />
          </FormField>

          <FormField label="Business Email" required data-id="element-10646">
            <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[var(--brand-primary)] focus:border-transparent" value={companyInfo.businessEmail} onChange={e => setCompanyInfo({
            ...companyInfo,
            businessEmail: e.target.value
          })} data-id="element-10647" />
          </FormField>

          <FormField label="Business Phone" required data-id="element-10648">
            <input type="tel" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[var(--brand-primary)] focus:border-transparent" value={companyInfo.businessPhone} onChange={e => setCompanyInfo({
            ...companyInfo,
            businessPhone: e.target.value
          })} data-id="element-10649" />
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-10650" />
    </div>;
  const renderBrandingSettings = () => <div className="space-y-6" data-id="element-10651">
      <div data-id="element-10652">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10653">Branding</h2>
        <p className="text-sm text-gray-600" data-id="element-10654">
          Customize your brand appearance across payment pages and receipts
        </p>
      </div>

      {/* Logo Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10655">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10656">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10657">
            <PaletteIcon size={18} className="text-gray-600" data-id="element-10658" />
            Logo & Visual Identity
          </h3>
        </div>
        <div className="p-6 space-y-6" data-id="element-10659">
          <FormField label="Company Logo" description="Upload your company logo (recommended: 200x60px, PNG or SVG)." data-id="element-10660">
            <div className="flex items-center gap-4" data-id="element-10661">
              {brandingSettings.logoUrl && <div className="border border-gray-200 rounded-lg p-4 bg-white" data-id="element-10662">
                  <img src={brandingSettings.logoUrl} alt="Company Logo" className="h-12 object-contain" data-id="element-10663" />
                </div>}
              <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center transition-colors" data-id="element-10664">
                <UploadIcon size={16} className="mr-2" data-id="element-10665" />
                Upload New Logo
              </button>
            </div>
          </FormField>

          <FormField label="Primary Brand Color" description="This color will be used for buttons and accents" data-id="element-10666">
            <div className="flex items-center gap-3" data-id="element-10667">
              <input type="color" className="h-10 w-20 border border-gray-300 rounded cursor-pointer" value={brandingSettings.primaryColor} onChange={e => setBrandingSettings({
              ...brandingSettings,
              primaryColor: e.target.value
            })} data-id="element-10668" />
              <input type="text" className="block w-32 px-3 py-2 border border-gray-300 rounded-md font-mono text-sm" value={brandingSettings.primaryColor} onChange={e => setBrandingSettings({
              ...brandingSettings,
              primaryColor: e.target.value
            })} data-id="element-10669" />
              <div className="flex-1 text-sm text-gray-600" data-id="element-10670">
                Current: BML Red
              </div>
            </div>
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-10671" />
    </div>;
  const renderGeneralSettings = () => <div className="space-y-6" data-id="element-10672">
      <div data-id="element-10673">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10674">
          General Settings
        </h2>
        <p className="text-sm text-gray-600" data-id="element-10675">
          Configure default behaviors and preferences for your payment system
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-10676">
        <div className="flex items-start gap-3" data-id="element-10677">
          <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-10678" />
          <div className="text-sm text-gray-700" data-id="element-10679">
            <p className="font-medium text-gray-900 mb-1" data-id="element-10680">
              Payment Link Defaults
            </p>
            <p className="text-xs" data-id="element-10681">
              These settings apply to all new payment links and invoices. You
              can override them individually when creating each payment.
            </p>
          </div>
        </div>
      </div>

      {/* Settings Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10682">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10683">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10684">
            <SettingsIcon size={18} className="text-gray-600" data-id="element-10685" />
            Default Configuration
          </h3>
        </div>
        <div className="p-6 space-y-5" data-id="element-10686">
          <FormField label="Default Expiry Days" description="Number of days before payment links expire" data-id="element-10687">
            <input type="number" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[var(--brand-primary)] focus:border-transparent" value={generalSettings.defaultExpiryDays} onChange={e => setGeneralSettings({
            ...generalSettings,
            defaultExpiryDays: parseInt(e.target.value)
          })} data-id="element-10688" />
          </FormField>

          <FormField label="Redirect URL" description="Where customers are sent after successful payment" data-id="element-10689">
            <input type="url" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[var(--brand-primary)] focus:border-transparent font-mono text-sm" value={generalSettings.redirectUrl} onChange={e => setGeneralSettings({
            ...generalSettings,
            redirectUrl: e.target.value
          })} data-id="element-10690" />
          </FormField>

          <div className="pt-2" data-id="element-10691">
            <label className="flex items-center gap-3 cursor-pointer" data-id="element-10692">
              <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-[var(--brand-primary)] focus:ring-[var(--brand-primary)]" checked={generalSettings.enableAutoReminders} onChange={e => setGeneralSettings({
              ...generalSettings,
              enableAutoReminders: e.target.checked
            })} data-id="element-10693" />
              <div data-id="element-10694">
                <p className="text-sm font-medium text-gray-900" data-id="element-10695">
                  Enable automatic payment reminders
                </p>
                <p className="text-xs text-gray-500" data-id="element-10696">
                  Send reminder emails for unpaid invoices
                </p>
              </div>
            </label>
          </div>
        </div>
      </div>

      <SaveButton data-id="element-10697" />
    </div>;
  const renderReceiptSettings = () => <div className="space-y-6" data-id="element-10698">
      <div data-id="element-10699">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10700">
          Receipt Settings
        </h2>
        <p className="text-sm text-gray-600" data-id="element-10701">
          Customize the receipts sent to your customers after payment
        </p>
      </div>

      {/* Receipt Customization Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10702">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10703">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10704">
            <MailIcon size={18} className="text-gray-600" data-id="element-10705" />
            Receipt Customization
          </h3>
        </div>
        <div className="p-6 space-y-5" data-id="element-10706">
          <div className="pb-2" data-id="element-10707">
            <label className="flex items-center gap-3 cursor-pointer" data-id="element-10708">
              <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-[var(--brand-primary)] focus:ring-[var(--brand-primary)]" checked={receiptSettings.showCompanyAddress} onChange={e => setReceiptSettings({
              ...receiptSettings,
              showCompanyAddress: e.target.checked
            })} data-id="element-10709" />
              <div data-id="element-10710">
                <p className="text-sm font-medium text-gray-900" data-id="element-10711">
                  Show company address on receipts
                </p>
                <p className="text-xs text-gray-500" data-id="element-10712">
                  Display your business address on customer receipts
                </p>
              </div>
            </label>
          </div>

          <FormField label="Company Address" description="This address will appear on receipts if enabled above" data-id="element-10713">
            <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" rows={3} value={receiptSettings.companyAddress} onChange={e => setReceiptSettings({
            ...receiptSettings,
            companyAddress: e.target.value
          })} data-id="element-10714" />
          </FormField>

          <FormField label="CC Email" description="Automatically CC this email on all receipts" data-id="element-10715">
            <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={receiptSettings.ccEmail} onChange={e => setReceiptSettings({
            ...receiptSettings,
            ccEmail: e.target.value
          })} data-id="element-10716" />
          </FormField>

          <FormField label="Footer Text" description="Custom message at the bottom of receipts" data-id="element-10717">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={receiptSettings.footerText} onChange={e => setReceiptSettings({
            ...receiptSettings,
            footerText: e.target.value
          })} data-id="element-10718" />
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-10719" />
    </div>;
  const renderPaymentProviders = () => <div className="space-y-6" data-id="element-10720">
      <div data-id="element-10721">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10722">
          Payment Providers
        </h2>
        <p className="text-sm text-gray-600" data-id="element-10723">
          Enable or disable payment methods available to your customers
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-10724">
        <div className="flex items-start gap-3" data-id="element-10725">
          <AlertCircleIcon size={20} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-10726" />
          <div className="text-sm text-gray-700" data-id="element-10727">
            <p className="font-medium text-gray-900 mb-1" data-id="element-10728">
              Payment Method Availability
            </p>
            <p className="text-xs" data-id="element-10729">
              Disabling a payment method will hide it from all payment pages.
              Customers won't be able to use disabled methods for new payments.
            </p>
          </div>
        </div>
      </div>

      {/* Card Payment Methods */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10730">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10731">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10732">
            <CreditCardIcon size={18} className="text-gray-600" data-id="element-10733" />
            Card Payments
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-10734">
          {paymentMethods.filter(m => m.category === 'card').map(method => {
          const Icon = method.icon;
          return <div key={method.id} className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10735">
                  <div className="flex items-center gap-3" data-id="element-10736">
                    <Icon size={20} className="text-gray-600" data-id="element-10737" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-10738">
                      {method.name}
                    </span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-10739">
                    <input type="checkbox" className="sr-only peer" checked={method.enabled} onChange={e => setPaymentMethods(paymentMethods.map(m => m.id === method.id ? {
                ...m,
                enabled: e.target.checked
              } : m))} data-id="element-10740" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10741"></div>
                  </label>
                </div>;
        })}
        </div>
      </div>

      {/* E-Wallet Payment Methods */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10742">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10743">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10744">
            <WalletIcon size={18} className="text-gray-600" data-id="element-10745" />
            E-Wallets & QR Payments
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-10746">
          {paymentMethods.filter(m => m.category === 'ewallet').map(method => {
          const Icon = method.icon;
          return <div key={method.id} className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10747">
                  <div className="flex items-center gap-3" data-id="element-10748">
                    <Icon size={20} className="text-gray-600" data-id="element-10749" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-10750">
                      {method.name}
                    </span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-10751">
                    <input type="checkbox" className="sr-only peer" checked={method.enabled} onChange={e => setPaymentMethods(paymentMethods.map(m => m.id === method.id ? {
                ...m,
                enabled: e.target.checked
              } : m))} data-id="element-10752" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10753"></div>
                  </label>
                </div>;
        })}
        </div>
      </div>

      {/* Online Banking */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10754">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10755">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10756">
            <BuildingIcon size={18} className="text-gray-600" data-id="element-10757" />
            Online Banking
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-10758">
          {paymentMethods.filter(m => m.category === 'banking').map(method => {
          const Icon = method.icon;
          return <div key={method.id} className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10759">
                  <div className="flex items-center gap-3" data-id="element-10760">
                    <Icon size={20} className="text-gray-600" data-id="element-10761" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-10762">
                      {method.name}
                    </span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-10763">
                    <input type="checkbox" className="sr-only peer" checked={method.enabled} onChange={e => setPaymentMethods(paymentMethods.map(m => m.id === method.id ? {
                ...m,
                enabled: e.target.checked
              } : m))} data-id="element-10764" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10765"></div>
                  </label>
                </div>;
        })}
        </div>
      </div>
    </div>;
  const renderUsers = () => <div className="space-y-6" data-id="element-10766">
      <div className="flex items-center justify-between" data-id="element-10767">
        <div data-id="element-10768">
          <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10769">
            Users & Permissions
          </h2>
          <p className="text-sm text-gray-600" data-id="element-10770">
            Manage team members and their access levels
          </p>
        </div>
        <div className="flex gap-3" data-id="element-10771">
          <button onClick={() => setShowRoleManagementModal(true)} className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-md flex items-center hover:bg-gray-50 transition-colors" data-id="element-10772">
            <ShieldIcon size={16} className="mr-2" data-id="element-10773" />
            Permissions
          </button>
          <button onClick={() => setShowInviteForm(!showInviteForm)} className="px-4 py-2 bg-[var(--brand-primary)] text-[#1A1A1A] font-medium rounded-md flex items-center hover:bg-[var(--brand-primaryhover)] transition-colors" data-id="element-10774">
            <PlusIcon size={16} className="mr-2" data-id="element-10775" />
            Invite User
          </button>
        </div>
      </div>

      {/* Invite Form */}
      {showInviteForm && <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10776">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10777">
            <h3 className="text-md font-semibold text-gray-900" data-id="element-10778">
              Invite New User
            </h3>
          </div>
          <div className="p-6 space-y-4" data-id="element-10779">
            <FormField label="Email Address" required data-id="element-10780">
              <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="user@example.com" data-id="element-10781" />
            </FormField>
            <FormField label="Role" required data-id="element-10782">
              <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={inviteRole} onChange={e => setInviteRole(e.target.value)} data-id="element-10783">
                <option value="Owner" data-id="element-10784">Owner</option>
                <option value="Merchant Management" data-id="element-10785">Merchant Management</option>
                <option value="Account Management" data-id="element-10786">Account Management</option>
                <option value="Sales" data-id="element-10787">Sales</option>
                <option value="Finance" data-id="element-10788">Finance</option>
                <option value="Operations" data-id="element-10789">Operations</option>
              </select>
              {inviteRole && rolePermissions[inviteRole as keyof typeof rolePermissions] && <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md" data-id="element-10790">
                    <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-10791">
                      {inviteRole}
                    </p>
                    <p className="text-xs text-gray-600" data-id="element-10792">
                      {rolePermissions[inviteRole as keyof typeof rolePermissions].description}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2" data-id="element-10793">
                      {Object.entries(rolePermissions[inviteRole as keyof typeof rolePermissions].permissions).slice(0, showAllInvitePerms ? Object.keys(rolePermissions[inviteRole as keyof typeof rolePermissions].permissions).length : 5).map(([section, perms]) => {
                const activePerms = Object.entries(perms).filter(([_, val]) => val).map(([key]) => key.charAt(0).toUpperCase() + key.slice(1));
                if (activePerms.length === 0) return null;
                return <span key={section} className="text-xs px-2 py-1 bg-white border border-blue-200 rounded text-gray-700" data-id="element-10794">
                              {section}: {activePerms.join(', ')}
                            </span>;
              })}
                      {Object.keys(rolePermissions[inviteRole as keyof typeof rolePermissions].permissions).length > 5 && <button type="button" onClick={() => setShowAllInvitePerms(!showAllInvitePerms)} className="text-xs px-2 py-1 text-blue-600 hover:text-blue-700 hover:underline font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded" data-id="element-10795">
                          {showAllInvitePerms ? 'Show less' : `+${Object.keys(rolePermissions[inviteRole as keyof typeof rolePermissions].permissions).length - 5} more`}
                        </button>}
                    </div>
                  </div>}
            </FormField>
            <div className="flex gap-3 pt-2" data-id="element-10796">
              <button onClick={() => {
            if (inviteEmail && inviteRole) {
              const newUser = {
                id: `user-${Date.now()}`,
                name: inviteEmail.split('@')[0],
                email: inviteEmail,
                role: inviteRole,
                status: 'Invited' as const
              };
              setUsers([...users, newUser]);
              setInviteEmail('');
              setInviteRole('Manager');
              setShowInviteForm(false);
            }
          }} className="px-4 py-2 bg-[var(--brand-primary)] text-[#1A1A1A] font-medium rounded-md hover:bg-[var(--brand-primaryhover)] transition-colors" data-id="element-10797">
                Send Invite
              </button>
              <button onClick={() => {
            setShowInviteForm(false);
            setInviteEmail('');
            setInviteRole('Operations');
          }} className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors" data-id="element-10798">
                Cancel
              </button>
            </div>
          </div>
        </div>}

      {/* Users Table */}
      <div className="border border-gray-200 rounded-lg" data-id="element-10799">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 rounded-t-lg" data-id="element-10800">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10801">
            <UsersIcon size={18} className="text-gray-600" data-id="element-10802" />
            Team Members ({users.length})
          </h3>
        </div>
        <div data-id="element-10803">
          <table className="w-full" data-id="element-10804">
            <thead className="bg-gray-50 border-b border-gray-200" data-id="element-10805">
              <tr data-id="element-10806">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10807">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10808">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10809">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10810">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10811">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white" data-id="element-10812">
              {users.map(u => <tr key={u.id} className="hover:bg-gray-50 transition-colors" data-id="element-10813">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-10814">
                    {u.name}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600" data-id="element-10815">{u.email}</td>
                  <td className="px-6 py-4 text-sm text-gray-600" data-id="element-10816">{u.role}</td>
                  <td className="px-6 py-4" data-id="element-10817">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${u.status === 'Active' ? 'bg-green-100 text-green-800' : u.status === 'Invited' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-10818">
                      {u.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right text-sm" data-id="element-10819">
                    <div className="flex items-center justify-end gap-2" data-id="element-10820">
                      {u.role === 'Owner' ? <span className="text-gray-400 text-xs" data-id="element-10821">Owner</span> : <div className="relative inline-block" data-id="element-10822">
                          <button type="button" aria-label={`Actions for ${u.name}`} aria-haspopup="menu" aria-expanded={openUserMenuId === u.id} onClick={() => setOpenUserMenuId(openUserMenuId === u.id ? null : u.id)} className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-10823">
                            <MoreHorizontalIcon size={18} data-id="element-10824" />
                          </button>
                          {openUserMenuId === u.id && <>
                              <div className="fixed inset-0 z-10" onClick={() => setOpenUserMenuId(null)} data-id="element-10825" />
                              <div role="menu" className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-md shadow-lg py-1 z-20" data-id="element-10826">
                                {u.status !== 'Invited' && <button role="menuitem" onClick={() => {
                          setEditingUser({
                            id: u.id,
                            name: u.name,
                            email: u.email,
                            role: u.role
                          });
                          setOpenUserMenuId(null);
                        }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-10827">
                                    Edit
                                  </button>}
                                {u.status === 'Active' && <button role="menuitem" onClick={() => {
                          setUsers(users.map(user => user.id === u.id ? {
                            ...user,
                            status: 'Disabled' as const
                          } : user));
                          setOpenUserMenuId(null);
                        }} className="w-full text-left px-3 py-2 text-sm text-amber-700 hover:bg-amber-50" data-id="element-10828">
                                    Disable
                                  </button>}
                                {u.status === 'Disabled' && <button role="menuitem" onClick={() => {
                          setUsers(users.map(user => user.id === u.id ? {
                            ...user,
                            status: 'Active' as const
                          } : user));
                          setOpenUserMenuId(null);
                        }} className="w-full text-left px-3 py-2 text-sm text-green-700 hover:bg-green-50" data-id="element-10829">
                                    Enable
                                  </button>}
                                <button role="menuitem" onClick={() => {
                          if (confirm(u.status === 'Invited' ? `Cancel invite for ${u.email}?` : `Are you sure you want to delete ${u.name}?`)) {
                            setUsers(users.filter(user => user.id !== u.id));
                          }
                          setOpenUserMenuId(null);
                        }} className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-10830">
                                  {u.status === 'Invited' ? 'Cancel Invite' : 'Delete'}
                                </button>
                              </div>
                            </>}
                        </div>}
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit User Modal */}
      {editingUser && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-10831">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden" data-id="element-10832">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-10833">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-10834">
                Edit Team Member
              </h3>
              <button onClick={() => setEditingUser(null)} className="text-gray-400 hover:text-gray-600" aria-label="Close" data-id="element-10835">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-10836">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" data-id="element-10837" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4" data-id="element-10838">
              <FormField label="Name" data-id="element-10839">
                <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={editingUser.name} onChange={e => setEditingUser({
              ...editingUser,
              name: e.target.value
            })} data-id="element-10840" />
              </FormField>
              <FormField label="Email" data-id="element-10841">
                <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={editingUser.email} onChange={e => setEditingUser({
              ...editingUser,
              email: e.target.value
            })} data-id="element-10842" />
              </FormField>
              <FormField label="Role" data-id="element-10843">
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={editingUser.role} onChange={e => setEditingUser({
              ...editingUser,
              role: e.target.value
            })} data-id="element-10844">
                  <option value="Merchant Management" data-id="element-10845">
                    Merchant Management
                  </option>
                  <option value="Account Management" data-id="element-10846">Account Management</option>
                  <option value="Sales" data-id="element-10847">Sales</option>
                  <option value="Finance" data-id="element-10848">Finance</option>
                  <option value="Operations" data-id="element-10849">Operations</option>
                </select>
              </FormField>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 bg-gray-50" data-id="element-10850">
              <button onClick={() => setEditingUser(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-10851">
                Cancel
              </button>
              <button onClick={() => {
            setUsers(users.map(user => user.id === editingUser.id ? {
              ...user,
              name: editingUser.name,
              email: editingUser.email,
              role: editingUser.role
            } : user));
            setEditingUser(null);
          }} className="px-4 py-2 text-sm font-medium bg-[var(--brand-primary)] text-[#1A1A1A] rounded-md hover:bg-[var(--brand-primaryhover)]" data-id="element-10852">
                Save Changes
              </button>
            </div>
          </div>
        </div>}

      {/* Role Management Modal */}
      {showRoleManagementModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-10853">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-id="element-10854">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-10855">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-10856">
                Role Permissions Matrix
              </h3>
              <button onClick={() => setShowRoleManagementModal(false)} className="text-gray-400 hover:text-gray-600" data-id="element-10857">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-10858">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" data-id="element-10859" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-auto flex-1" data-id="element-10860">
              <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-10861">
                {Object.entries(rolePermissions).map(([role, config]) => <div key={role} className="border border-gray-200 rounded-lg p-4" data-id="element-10862">
                    <h4 className="font-semibold text-gray-900 mb-1" data-id="element-10863">{role}</h4>
                    <p className="text-xs text-gray-600" data-id="element-10864">
                      {config.description}
                    </p>
                  </div>)}
              </div>
              <div className="overflow-x-auto" data-id="element-10865">
                <table className="w-full border border-gray-200 rounded-lg" data-id="element-10866">
                  <thead className="bg-gray-50" data-id="element-10867">
                    <tr data-id="element-10868">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-10869">
                        Section
                      </th>
                      {Object.keys(rolePermissions).map(role => <th key={role} colSpan={5} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-l border-gray-200" data-id="element-10870">
                          {role}
                        </th>)}
                    </tr>
                    <tr data-id="element-10871">
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-10872"></th>
                      {Object.keys(rolePermissions).map(role => <Fragment key={role} data-id="element-10873">
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-l border-gray-200" data-id="element-10874">
                            R
                          </th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-10875">
                            W
                          </th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-10876">
                            M
                          </th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-10877">
                            I
                          </th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-10878">
                            A
                          </th>
                        </Fragment>)}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-10879">
                    {Object.keys(rolePermissions.Owner.permissions).map(section => <tr key={section} className="hover:bg-gray-50" data-id="element-10880">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-10881">
                            {section}
                          </td>
                          {Object.entries(rolePermissions).map(([role, config]) => {
                    const perms = config.permissions[section as keyof typeof config.permissions];
                    return <Fragment key={role} data-id="element-10882">
                                  <td className="px-2 py-3 text-center border-l border-gray-200" data-id="element-10883">
                                    {perms.read ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-10884" /> : <span className="text-gray-300" data-id="element-10885">—</span>}
                                  </td>
                                  <td className="px-2 py-3 text-center" data-id="element-10886">
                                    {perms.write ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-10887" /> : <span className="text-gray-300" data-id="element-10888">—</span>}
                                  </td>
                                  <td className="px-2 py-3 text-center" data-id="element-10889">
                                    {perms.modify ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-10890" /> : <span className="text-gray-300" data-id="element-10891">—</span>}
                                  </td>
                                  <td className="px-2 py-3 text-center" data-id="element-10892">
                                    {perms.insert ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-10893" /> : <span className="text-gray-300" data-id="element-10894">—</span>}
                                  </td>
                                  <td className="px-2 py-3 text-center" data-id="element-10895">
                                    {perms.approve ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-10896" /> : <span className="text-gray-300" data-id="element-10897">—</span>}
                                  </td>
                                </Fragment>;
                  })}
                        </tr>)}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 text-xs text-gray-500" data-id="element-10898">
                <p className="font-medium mb-1" data-id="element-10899">Permission Legend:</p>
                <div className="flex flex-wrap gap-4" data-id="element-10900">
                  <span data-id="element-10901">
                    <strong data-id="element-10902">R</strong> = Read
                  </span>
                  <span data-id="element-10903">
                    <strong data-id="element-10904">W</strong> = Write
                  </span>
                  <span data-id="element-10905">
                    <strong data-id="element-10906">M</strong> = Modify
                  </span>
                  <span data-id="element-10907">
                    <strong data-id="element-10908">I</strong> = Insert
                  </span>
                  <span data-id="element-10909">
                    <strong data-id="element-10910">A</strong> = Approve
                  </span>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end" data-id="element-10911">
              <button onClick={() => setShowRoleManagementModal(false)} className="px-4 py-2 bg-[var(--brand-primary)] text-[#1A1A1A] font-medium rounded-md hover:bg-[var(--brand-primaryhover)] transition-colors" data-id="element-10912">
                Close
              </button>
            </div>
          </div>
        </div>}
    </div>;
  const renderTerms = () => <div className="space-y-6" data-id="element-10913">
      <div data-id="element-10914">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10915">
          Terms & Privacy
        </h2>
        <p className="text-sm text-gray-600" data-id="element-10916">
          Configure legal terms and privacy policy shown to customers
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-10917">
        <div className="flex items-start gap-3" data-id="element-10918">
          <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-10919" />
          <div className="text-sm text-gray-700" data-id="element-10920">
            <p className="font-medium text-gray-900 mb-1" data-id="element-10921">Legal Compliance</p>
            <p className="text-xs" data-id="element-10922">
              These terms are displayed to customers during checkout. Ensure
              they comply with Malaysian data protection and consumer laws.
            </p>
          </div>
        </div>
      </div>

      {/* Terms Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10923">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10924">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10925">
            <FileTextIcon size={18} className="text-gray-600" data-id="element-10926" />
            Terms & Conditions
          </h3>
        </div>
        <div className="p-6" data-id="element-10927">
          <FormField label="Terms Text" description="Displayed on payment pages and checkout" data-id="element-10928">
            <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent font-sans" rows={6} value={termsSettings.termsText} onChange={e => setTermsSettings({
            ...termsSettings,
            termsText: e.target.value
          })} data-id="element-10929" />
          </FormField>
        </div>
      </div>

      {/* Privacy Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10930">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10931">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10932">
            <ShieldIcon size={18} className="text-gray-600" data-id="element-10933" />
            Privacy Policy
          </h3>
        </div>
        <div className="p-6" data-id="element-10934">
          <FormField label="Privacy Policy Text" description="Information about how customer data is handled" data-id="element-10935">
            <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent font-sans" rows={6} value={termsSettings.privacyPolicy} onChange={e => setTermsSettings({
            ...termsSettings,
            privacyPolicy: e.target.value
          })} data-id="element-10936" />
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-10937" />
    </div>;
  const renderFields = () => <div className="space-y-6" data-id="element-10938">
      <div data-id="element-10939">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10940">
          Information Fields
        </h2>
        <p className="text-sm text-gray-600" data-id="element-10941">
          Configure metadata fields collected at checkout
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-10942">
        <div className="flex items-start gap-3" data-id="element-10943">
          <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-10944" />
          <div className="text-sm text-gray-700" data-id="element-10945">
            <p className="font-medium text-gray-900 mb-1" data-id="element-10946">
              Custom Checkout Fields
            </p>
            <p className="text-xs" data-id="element-10947">
              Add custom fields to collect additional information from customers
              during checkout. This data will be attached to payment records and
              can be used for reporting or integration purposes.
            </p>
          </div>
        </div>
      </div>

      {/* Fields Editor Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10948">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10949">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10950">
            <FormInputIcon size={18} className="text-gray-600" data-id="element-10951" />
            Metadata Fields
          </h3>
        </div>
        <div className="p-6" data-id="element-10952">
          <MetadataEditor metadata={presetMetadataFields} onChange={setPresetMetadataFields} data-id="element-10953" />
        </div>
      </div>
    </div>;
  const renderNotifications = () => <div className="space-y-6" data-id="element-10954">
      <div data-id="element-10955">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10956">
          Notifications
        </h2>
        <p className="text-sm text-gray-600" data-id="element-10957">
          Manage how you receive alerts about payments and account activity
        </p>
      </div>

      {/* Notification Channels Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10958">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10959">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10960">
            <BellIcon size={18} className="text-gray-600" data-id="element-10961" />
            Notification Channels
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-10962">
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10963">
            <div data-id="element-10964">
              <p className="text-sm font-medium text-gray-900" data-id="element-10965">
                Email Notifications
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10966">
                Receive alerts via email
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10967">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.emailNotifications} onChange={e => setNotificationSettings({
              ...notificationSettings,
              emailNotifications: e.target.checked
            })} data-id="element-10968" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10969"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10970">
            <div data-id="element-10971">
              <p className="text-sm font-medium text-gray-900" data-id="element-10972">
                SMS Notifications
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10973">
                Receive alerts via text message
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10974">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.smsNotifications} onChange={e => setNotificationSettings({
              ...notificationSettings,
              smsNotifications: e.target.checked
            })} data-id="element-10975" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10976"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Event Notifications Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10977">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10978">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10979">
            <AlertCircleIcon size={18} className="text-gray-600" data-id="element-10980" />
            Event Notifications
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-10981">
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10982">
            <div data-id="element-10983">
              <p className="text-sm font-medium text-gray-900" data-id="element-10984">
                Payment Received
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10985">
                When a customer completes payment
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10986">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.paymentReceived} onChange={e => setNotificationSettings({
              ...notificationSettings,
              paymentReceived: e.target.checked
            })} data-id="element-10987" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10988"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10989">
            <div data-id="element-10990">
              <p className="text-sm font-medium text-gray-900" data-id="element-10991">
                Payment Failed
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10992">
                When a payment attempt fails
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10993">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.paymentFailed} onChange={e => setNotificationSettings({
              ...notificationSettings,
              paymentFailed: e.target.checked
            })} data-id="element-10994" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10995"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10996">
            <div data-id="element-10997">
              <p className="text-sm font-medium text-gray-900" data-id="element-10998">
                Refund Processed
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10999">
                When a refund is completed
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-11000">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.refundProcessed} onChange={e => setNotificationSettings({
              ...notificationSettings,
              refundProcessed: e.target.checked
            })} data-id="element-11001" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-11002"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-11003">
            <div data-id="element-11004">
              <p className="text-sm font-medium text-gray-900" data-id="element-11005">
                Dispute Opened
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-11006">
                When a customer files a dispute
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-11007">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.disputeOpened} onChange={e => setNotificationSettings({
              ...notificationSettings,
              disputeOpened: e.target.checked
            })} data-id="element-11008" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-11009"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-11010">
            <div data-id="element-11011">
              <p className="text-sm font-medium text-gray-900" data-id="element-11012">
                Settlement Completed
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-11013">
                When funds are transferred to your account
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-11014">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.settlementCompleted} onChange={e => setNotificationSettings({
              ...notificationSettings,
              settlementCompleted: e.target.checked
            })} data-id="element-11015" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-11016"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-11017">
            <div data-id="element-11018">
              <p className="text-sm font-medium text-gray-900" data-id="element-11019">Low Balance</p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-11020">
                When your account balance is low
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-11021">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.lowBalance} onChange={e => setNotificationSettings({
              ...notificationSettings,
              lowBalance: e.target.checked
            })} data-id="element-11022" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-11023"></div>
            </label>
          </div>
        </div>
      </div>
    </div>;
  const renderSecurity = () => <div className="space-y-6" data-id="element-11024">
      <div data-id="element-11025">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-11026">Security</h2>
        <p className="text-sm text-gray-600" data-id="element-11027">
          Manage security settings and access controls for your account
        </p>
      </div>

      {/* Security Status Banner */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-id="element-11028">
        <div className="flex items-start gap-3" data-id="element-11029">
          <CheckCircleIcon size={20} className="text-green-600 flex-shrink-0 mt-0.5" data-id="element-11030" />
          <div className="text-sm text-gray-700" data-id="element-11031">
            <p className="font-medium text-gray-900 mb-1" data-id="element-11032">
              Security Status: Protected
            </p>
            <p className="text-xs" data-id="element-11033">
              Your account is secured with two-factor authentication and session
              management. Keep these settings enabled for maximum protection.
            </p>
          </div>
        </div>
      </div>

      {/* Authentication Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11034">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-11035">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-11036">
            <LockIcon size={18} className="text-gray-600" data-id="element-11037" />
            Authentication & Access
          </h3>
        </div>
        <div className="p-6 space-y-5" data-id="element-11038">
          <div className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-4" data-id="element-11039">
            <div data-id="element-11040">
              <p className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-11041">
                Two-Factor Authentication
                {securitySettings.twoFactorEnabled && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-11042">
                    Enabled
                  </span>}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-11043">
                Adds an extra layer of security to your account
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-11044">
              <input type="checkbox" className="sr-only peer" checked={securitySettings.twoFactorEnabled} onChange={e => setSecuritySettings({
              ...securitySettings,
              twoFactorEnabled: e.target.checked
            })} data-id="element-11045" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-11046"></div>
            </label>
          </div>

          <FormField label="Session Timeout (minutes)" description="Automatically log out after this period of inactivity" data-id="element-11047">
            <input type="number" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={securitySettings.sessionTimeout} onChange={e => setSecuritySettings({
            ...securitySettings,
            sessionTimeout: parseInt(e.target.value)
          })} data-id="element-11048" />
          </FormField>
        </div>
      </div>

      {/* IP Whitelist Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11049">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-11050">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-11051">
            <ShieldIcon size={18} className="text-gray-600" data-id="element-11052" />
            IP Whitelist
          </h3>
        </div>
        <div className="p-6" data-id="element-11053">
          <FormField label="Allowed IP Addresses" description="Restrict API access to specific IP addresses (one per line). Leave empty to allow all IPs." data-id="element-11054">
            <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" rows={4} value={securitySettings.ipWhitelist} onChange={e => setSecuritySettings({
            ...securitySettings,
            ipWhitelist: e.target.value
          })} placeholder="192.168.1.1&#10;10.0.0.1" data-id="element-11055" />
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-11056" />
    </div>;
  const renderPayoutSettings = () => {
    const isMinBalanceInvalid = minBalanceEnabled && (!minBalanceAmount || Number(minBalanceAmount) <= 0);
    let pendingPayoutUpdate: {
      bank?: string;
      maskedAccount?: string;
      submittedAt?: string;
    } | null = null;
    try {
      const raw = typeof window !== 'undefined' ? sessionStorage.getItem('pendingPayoutUpdate') : null;
      if (raw) pendingPayoutUpdate = JSON.parse(raw);
    } catch {
      pendingPayoutUpdate = null;
    }
    return <div className="space-y-6" data-id="element-11057">
        <div data-id="element-11058">
          <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-11059">
            Payout Settings
          </h2>
          <p className="text-sm text-gray-600" data-id="element-11060">
            Configure bank accounts, minimum balance, and payout preferences
          </p>
        </div>

        {/* Settlement Currencies Card */}
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11061">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-11062">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-11063">
              <BuildingIcon size={18} className="text-gray-600" data-id="element-11064" />
              Settlement Currencies & Bank Accounts
            </h3>
            <p className="text-sm text-gray-500 mt-1" data-id="element-11065">
              Configure bank accounts for receiving payouts in different
              currencies
            </p>
          </div>

          <div className="overflow-x-auto" data-id="element-11066">
            <table className="w-full text-left" data-id="element-11067">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-11068">
                <tr data-id="element-11069">
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11070">
                    Currency
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11071">
                    Payout Bank Account
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11072">
                    <div className="flex items-center gap-1.5" data-id="element-11073">
                      Payout Fee
                      <InfoIcon size={14} className="text-gray-400" data-id="element-11074" />
                    </div>
                  </th>
                  <th className="px-6 py-3" data-id="element-11075"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-11076">
                <tr className="hover:bg-gray-50 transition-colors" data-id="element-11077">
                  <td className="px-6 py-4" data-id="element-11078">
                    <div className="flex items-center gap-2" data-id="element-11079">
                      <span className="text-sm font-medium text-gray-900" data-id="element-11080">
                        MYR
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-11081">
                        Default
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-11082">
                    <div className="flex flex-col gap-1.5" data-id="element-11083">
                      <div className="flex items-center gap-3" data-id="element-11084">
                        <div className="w-8 h-8 rounded bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0" data-id="element-11085">
                          <BuildingIcon size={16} className="text-gray-500" data-id="element-11086" />
                        </div>
                        <div className="flex items-center gap-2 flex-wrap" data-id="element-11087">
                          <span className="text-sm text-gray-900" data-id="element-11088">
                            {BRAND.name}
                          </span>
                          <span className="text-sm text-gray-500 font-mono" data-id="element-11089">
                            ••••5678
                          </span>
                        </div>
                      </div>
                      {pendingPayoutUpdate && <div className="ml-11 flex items-center gap-2 flex-wrap" data-id="element-11090">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200" data-id="element-11091">
                            <ClockIcon size={12} data-id="element-11092" />
                            Pending Bank Review
                          </span>
                          <span className="text-xs text-gray-600" data-id="element-11093">
                            New: {pendingPayoutUpdate.bank}{' '}
                            <span className="font-mono" data-id="element-11094">
                              {pendingPayoutUpdate.maskedAccount}
                            </span>
                          </span>
                        </div>}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-11095">
                    <span className="text-sm text-gray-600" data-id="element-11096">Free</span>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-11097">
                    <div className="flex items-center justify-end gap-2" data-id="element-11098">
                      <button onClick={() => navigate('/settings/payout-account/edit')} disabled={!!pendingPayoutUpdate} title={pendingPayoutUpdate ? 'An update is pending bank review' : 'Edit payout account'} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-11099">
                        Edit
                      </button>
                      <button aria-label="More options" className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors" data-id="element-11100">
                        <MoreHorizontalIcon size={18} data-id="element-11101" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50" data-id="element-11102">
            <button className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors" data-id="element-11103">
              + Add a settlement currency
            </button>
          </div>
        </div>

        {/* Minimum Balance Card */}
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11104">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-11105">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-11106">
              <DollarSignIcon size={18} className="text-gray-600" data-id="element-11107" />
              Minimum Balance
            </h3>
            <p className="text-sm text-gray-500 mt-1" data-id="element-11108">
              Keep a minimum amount in your account to cover refunds and
              chargebacks
            </p>
          </div>

          <div className="p-6" data-id="element-11109">
            <div className="flex items-start gap-4" data-id="element-11110">
              <label className="relative inline-flex items-center cursor-pointer mt-0.5" data-id="element-11111">
                <input type="checkbox" className="sr-only peer" checked={minBalanceEnabled} onChange={e => setMinBalanceEnabled(e.target.checked)} data-id="element-11112" />
                <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-11113"></div>
              </label>

              <div className="flex-1" data-id="element-11114">
                <p className="text-sm font-medium text-gray-900" data-id="element-11115">
                  Keep a minimum amount in your account
                </p>
                <p className="text-sm text-gray-500 mt-0.5 mb-4" data-id="element-11116">
                  We'll adjust your payouts so you always maintain this balance
                </p>

                {minBalanceEnabled && <div className="mb-4" data-id="element-11117">
                    <div className="relative max-w-xs" data-id="element-11118">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-11119">
                        <span className="text-gray-500 text-sm" data-id="element-11120">MYR</span>
                      </div>
                      <input type="number" value={minBalanceAmount} onChange={e => setMinBalanceAmount(e.target.value)} className={`block w-full pl-14 pr-3 py-2 text-sm rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent ${isMinBalanceInvalid ? 'border-red-300 text-red-900' : 'border-gray-300'}`} placeholder="0.00" data-id="element-11121" />
                    </div>
                    {isMinBalanceInvalid && <p className="mt-2 text-sm text-red-600 flex items-center gap-1.5" data-id="element-11122">
                        <InfoIcon size={14} data-id="element-11123" />
                        Amount must be greater than 0
                      </p>}
                  </div>}
              </div>
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3" data-id="element-11124">
            <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-11125">
              Cancel
            </button>
            <button disabled={isMinBalanceInvalid} className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${isMinBalanceInvalid ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-[var(--brand-primary)] hover:bg-[var(--brand-primaryhover)] text-[#1A1A1A]'}`} data-id="element-11126">
              Save
            </button>
          </div>
        </div>

        {/* Statement Descriptor Card */}
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11127">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-11128">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-11129">
              <FileTextIcon size={18} className="text-gray-600" data-id="element-11130" />
              Payout Statement Descriptor
            </h3>
            <p className="text-sm text-gray-500 mt-1" data-id="element-11131">
              Customize how payouts appear on your bank statement
            </p>
          </div>

          <div className="p-6 space-y-6" data-id="element-11132">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3" data-id="element-11133">
              <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-11134" />
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-11135">
                Set a custom statement descriptor to help identify payouts in
                your bank account. This will appear on your bank statement for
                all payouts.
              </p>
            </div>

            <FormField label="Statement Descriptor" description="This text will appear on your bank statement" data-id="element-11136">
              <input type="text" value={payoutDescriptor} onChange={e => setPayoutDescriptor(e.target.value)} placeholder={`Example - ${companyInfo.businessName}`} className="block w-full max-w-md px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" data-id="element-11137" />
            </FormField>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3" data-id="element-11138">
            <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-11139">
              Cancel
            </button>
            <button className="px-4 py-2 text-sm font-medium bg-[var(--brand-primary)] hover:bg-[var(--brand-primaryhover)] text-[#1A1A1A] rounded-md transition-colors" data-id="element-11140">
              Save
            </button>
          </div>
        </div>
      </div>;
  };
  // MDR row component
  const MdrRow = ({
    type,
    tier,
    range,
    ic,
    sf,
    am,
    total,
    color
  }: any) => <tr className={color} data-id="element-11141">
      <td className="px-4 py-2 text-sm text-gray-900" data-id="element-11142">{type}</td>
      <td className="px-4 py-2 text-sm text-gray-900" data-id="element-11143">{tier}</td>
      <td className="px-4 py-2 text-sm text-gray-600" data-id="element-11144">{range}</td>
      <td className="px-4 py-2 text-sm text-gray-600" data-id="element-11145">{ic}</td>
      <td className="px-4 py-2 text-sm text-gray-600" data-id="element-11146">{sf}</td>
      <td className="px-4 py-2 text-sm text-gray-600" data-id="element-11147">{am}</td>
      <td className="px-4 py-2 text-sm font-medium text-gray-900" data-id="element-11148">{total}</td>
    </tr>;
  const FixedFee = ({
    label,
    sublabel,
    amount
  }: {
    label: string;
    sublabel: string;
    amount: number | string;
  }) => <div className="flex items-center justify-between" data-id="element-11149">
      <div data-id="element-11150">
        <p className="text-sm text-gray-900 font-medium" data-id="element-11151">{label}</p>
        <p className="text-xs text-gray-500" data-id="element-11152">{sublabel}</p>
      </div>
      <span className="text-sm font-semibold text-gray-900" data-id="element-11153">
        {typeof amount === 'number' ? formatCurrencyAmount(amount, {
        currency
      }) : amount}
      </span>
    </div>;
  const renderSubscriptionPlanSettings = () => <div data-id="element-11154">
      <h2 className="text-lg font-medium text-gray-800 mb-4" data-id="element-11155">
        Subscription Plan
      </h2>
      <div className="space-y-6" data-id="element-11156">
        {/* Supported Payment Methods */}
        <div className="border-b border-gray-200 pb-6" data-id="element-11157">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center" data-id="element-11158">
            <CreditCardIcon size={16} className="mr-2 text-blue-600" data-id="element-11159" />
            Supported Payment Methods
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-11160">
            Payment methods enabled for your account with their merchant
            discount rates
          </p>

          {/* Card Payments */}
          <div className="mb-6" data-id="element-11161">
            <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-11162">
              Card Payments
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-11163">
              {/* Visa */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11164">
                <div className="bg-blue-50 px-4 py-3 border-b border-blue-200" data-id="element-11165">
                  <div className="flex items-center justify-between" data-id="element-11166">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11167">
                      Visa
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-11168">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4 space-y-3" data-id="element-11169">
                  <div className="flex items-center justify-between" data-id="element-11170">
                    <span className="text-xs text-gray-600" data-id="element-11171">Debit Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11172">
                      0.80% - 1.30%
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-11173">
                    <span className="text-xs text-gray-600" data-id="element-11174">Credit Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11175">
                      1.10% - 1.70%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-11176">
                    <p className="text-xs text-gray-500 mb-2" data-id="element-11177">
                      + MYR 0.50 per transaction
                    </p>
                    <div className="bg-gray-50 rounded p-2 space-y-1" data-id="element-11178">
                      <p className="text-xs text-gray-600" data-id="element-11179">
                        <span className="font-medium" data-id="element-11180">MYR 1-1,000:</span> 0.80%
                      </p>
                      <p className="text-xs text-gray-600" data-id="element-11181">
                        <span className="font-medium" data-id="element-11182">MYR 1,000+:</span> 1.30%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mastercard */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11183">
                <div className="bg-orange-50 px-4 py-3 border-b border-orange-200" data-id="element-11184">
                  <div className="flex items-center justify-between" data-id="element-11185">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11186">
                      Mastercard
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-11187">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4 space-y-3" data-id="element-11188">
                  <div className="flex items-center justify-between" data-id="element-11189">
                    <span className="text-xs text-gray-600" data-id="element-11190">Debit Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11191">
                      0.85% - 1.35%
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-11192">
                    <span className="text-xs text-gray-600" data-id="element-11193">Credit Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11194">
                      1.15% - 1.75%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-11195">
                    <p className="text-xs text-gray-500 mb-2" data-id="element-11196">
                      + MYR 0.50 per transaction
                    </p>
                    <div className="bg-gray-50 rounded p-2 space-y-1" data-id="element-11197">
                      <p className="text-xs text-gray-600" data-id="element-11198">
                        <span className="font-medium" data-id="element-11199">MYR 1-500:</span> 0.85%
                      </p>
                      <p className="text-xs text-gray-600" data-id="element-11200">
                        <span className="font-medium" data-id="element-11201">MYR 500+:</span> 1.35%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* American Express */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11202">
                <div className="bg-indigo-50 px-4 py-3 border-b border-indigo-200" data-id="element-11203">
                  <div className="flex items-center justify-between" data-id="element-11204">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11205">
                      American Express
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-11206">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4 space-y-3" data-id="element-11207">
                  <div className="flex items-center justify-between" data-id="element-11208">
                    <span className="text-xs text-gray-600" data-id="element-11209">All Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11210">
                      2.40% - 2.60%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-11211">
                    <p className="text-xs text-gray-500" data-id="element-11212">
                      + MYR 0.75 per transaction
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* E-Wallets & Online Banking */}
          <div data-id="element-11213">
            <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-11214">
              E-Wallets & Online Banking
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-11215">
              {/* Touch 'n Go */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11216">
                <div className="bg-blue-50 px-4 py-3 border-b border-blue-200" data-id="element-11217">
                  <div className="flex items-center justify-between" data-id="element-11218">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11219">
                      Touch 'n Go
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-11220">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4" data-id="element-11221">
                  <div className="flex items-center justify-between mb-2" data-id="element-11222">
                    <span className="text-xs text-gray-600" data-id="element-11223">MDR</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11224">
                      1.50%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-11225">
                    <p className="text-xs text-gray-500" data-id="element-11226">
                      + MYR 0.30 per transaction
                    </p>
                  </div>
                </div>
              </div>

              {/* GrabPay */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11227">
                <div className="bg-green-50 px-4 py-3 border-b border-green-200" data-id="element-11228">
                  <div className="flex items-center justify-between" data-id="element-11229">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11230">
                      GrabPay
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-11231">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4" data-id="element-11232">
                  <div className="flex items-center justify-between mb-2" data-id="element-11233">
                    <span className="text-xs text-gray-600" data-id="element-11234">MDR</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11235">
                      1.50%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-11236">
                    <p className="text-xs text-gray-500" data-id="element-11237">
                      + MYR 0.30 per transaction
                    </p>
                  </div>
                </div>
              </div>

              {/* FPX */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11238">
                <div className="bg-purple-50 px-4 py-3 border-b border-purple-200" data-id="element-11239">
                  <div className="flex items-center justify-between" data-id="element-11240">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11241">
                      FPX
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-11242">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4" data-id="element-11243">
                  <div className="flex items-center justify-between mb-2" data-id="element-11244">
                    <span className="text-xs text-gray-600" data-id="element-11245">MDR</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11246">
                      1.20%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-11247">
                    <p className="text-xs text-gray-500" data-id="element-11248">
                      + MYR 0.40 per transaction
                    </p>
                  </div>
                </div>
              </div>

              {/* DuitNow QR */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11249">
                <div className="bg-amber-50 px-4 py-3 border-b border-amber-200" data-id="element-11250">
                  <div className="flex items-center justify-between" data-id="element-11251">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11252">
                      DuitNow QR
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-11253">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4" data-id="element-11254">
                  <div className="flex items-center justify-between mb-2" data-id="element-11255">
                    <span className="text-xs text-gray-600" data-id="element-11256">MDR</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11257">
                      0.80%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-11258">
                    <p className="text-xs text-gray-500" data-id="element-11259">
                      + MYR 0.25 per transaction
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-11260">
            <div className="flex items-start gap-2" data-id="element-11261">
              <InfoIcon size={16} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-11262" />
              <div className="text-sm text-gray-700" data-id="element-11263">
                <p className="font-medium text-gray-900 mb-1" data-id="element-11264">Pricing Notes:</p>
                <ul className="list-disc list-inside space-y-1 text-xs" data-id="element-11265">
                  <li data-id="element-11266">
                    Card rates vary based on card type (debit/credit) and
                    issuing bank
                  </li>
                  <li data-id="element-11267">
                    Lower rates apply to CIMB-issued cards (on-us transactions)
                  </li>
                  <li data-id="element-11268">All fees subject to applicable taxes (SST 6%)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Transaction Fees & Additional Charges */}
        <div className="border-b border-gray-200 pb-6" data-id="element-11269">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center" data-id="element-11270">
            <TagIcon size={16} className="mr-2 text-blue-600" data-id="element-11271" />
            Transaction Fees & Additional Charges
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-11272">
            Fixed fees and additional charges per transaction or event
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-11273">
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11274">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200" data-id="element-11275">
                <h4 className="text-sm font-semibold text-gray-900" data-id="element-11276">
                  Card Transaction Fees
                </h4>
              </div>
              <div className="p-4 space-y-3" data-id="element-11277">
                <FixedFee label="Visa/Mastercard" sublabel="Per successful transaction" amount={0.5} data-id="element-11278" />
                <FixedFee label="American Express" sublabel="Per successful transaction" amount={0.75} data-id="element-11279" />
                <FixedFee label="Authorization Fee" sublabel="Per authorization attempt" amount={0.2} data-id="element-11280" />
              </div>
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11281">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200" data-id="element-11282">
                <h4 className="text-sm font-semibold text-gray-900" data-id="element-11283">
                  E-Wallet & Online Banking
                </h4>
              </div>
              <div className="p-4 space-y-3" data-id="element-11284">
                <FixedFee label="Touch 'n Go / GrabPay" sublabel="Per successful transaction" amount={0.3} data-id="element-11285" />
                <FixedFee label="FPX (Online Banking)" sublabel="Per successful transaction" amount={0.4} data-id="element-11286" />
                <FixedFee label="DuitNow QR" sublabel="Per successful transaction" amount={0.25} data-id="element-11287" />
              </div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11288">
            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200" data-id="element-11289">
              <h4 className="text-sm font-semibold text-gray-900" data-id="element-11290">
                Additional Service Fees
              </h4>
            </div>
            <table className="w-full text-left" data-id="element-11291">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-11292">
                <tr data-id="element-11293">
                  <th className="px-4 py-2 text-xs font-medium text-gray-500" data-id="element-11294">
                    Service
                  </th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500" data-id="element-11295">
                    Description
                  </th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500 text-right" data-id="element-11296">
                    Fee
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-11297">
                <tr data-id="element-11298">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-11299">
                    Refund Processing
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600" data-id="element-11300">
                    Per refund transaction
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right" data-id="element-11301">
                    {formatCurrencyAmount(1.0, {
                    currency
                  })}
                  </td>
                </tr>
                <tr data-id="element-11302">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-11303">
                    Chargeback Fee
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600" data-id="element-11304">
                    Per chargeback filed
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right" data-id="element-11305">
                    {formatCurrencyAmount(50.0, {
                    currency
                  })}
                  </td>
                </tr>
                <tr data-id="element-11306">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-11307">
                    Chargeback Reversal
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600" data-id="element-11308">
                    When chargeback is won
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-green-600 text-right" data-id="element-11309">{`-${formatCurrencyAmount(50.0, {
                    currency
                  })}`}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-11310">
            <div className="flex items-start gap-2" data-id="element-11311">
              <InfoIcon size={16} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-11312" />
              <div className="text-sm text-gray-700" data-id="element-11313">
                <p className="font-medium text-gray-900 mb-1" data-id="element-11314">Fee Structure:</p>
                <ul className="list-disc list-inside space-y-1 text-xs" data-id="element-11315">
                  <li data-id="element-11316">
                    <span className="font-medium" data-id="element-11317">Total cost</span> =
                    (Transaction Amount × MDR%) + Fixed Fee
                  </li>
                  <li data-id="element-11318">
                    Example: MYR 100 Visa on-us = (MYR 100 × 1.10%) + MYR 0.50 =
                    MYR 1.60
                  </li>
                  <li data-id="element-11319">All fees subject to applicable taxes (SST 6%)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Fees */}
        <div className="border-b border-gray-200 pb-6" data-id="element-11320">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center" data-id="element-11321">
            <DollarSignIcon size={16} className="mr-2 text-blue-600" data-id="element-11322" />
            Subscription Fees
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-11323">
            <div className="border border-gray-200 rounded-lg p-4" data-id="element-11324">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-11325">
                Annual Rental
              </p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-11326">
                {formatCurrencyAmount(2400, {
                currency
              })}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-11327">Billed annually</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4" data-id="element-11328">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-11329">
                Monthly Equivalent
              </p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-11330">
                {formatCurrencyAmount(200, {
                currency
              })}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-11331">
                MYR 2,400 / 12 months
              </p>
            </div>
          </div>
        </div>

        {/* Last Payment */}
        <div data-id="element-11332">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center gap-2" data-id="element-11333">
            <ClockIcon size={16} className="mr-2 text-blue-600" data-id="element-11334" />
            Last Subscription Payment
          </h3>
          <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-between" data-id="element-11335">
            <div data-id="element-11336">
              <p className="text-sm font-medium text-gray-900" data-id="element-11337">
                Annual Subscription Renewal
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-11338">
                Payment Date: January 15, 2026
              </p>
            </div>
            <div className="text-right" data-id="element-11339">
              <p className="text-lg font-bold text-gray-900" data-id="element-11340">
                {formatCurrencyAmount(2400, {
                currency
              })}
              </p>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 mt-1" data-id="element-11341">
                Paid
              </span>
            </div>
          </div>
          <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3" data-id="element-11342">
            <p className="text-xs text-gray-600" data-id="element-11343">
              <span className="font-medium" data-id="element-11344">Next renewal:</span> January 15,
              2027
            </p>
          </div>
        </div>
      </div>
    </div>;
  const renderActivityLogs = () => {
    const filteredLogs = activityLogs.filter(log => {
      if (logFilters.search && !log.description.toLowerCase().includes(logFilters.search.toLowerCase()) && !log.user.name.toLowerCase().includes(logFilters.search.toLowerCase())) return false;
      if (logFilters.user !== 'all' && log.user.id !== logFilters.user) return false;
      if (logFilters.section !== 'all' && log.section !== logFilters.section) return false;
      if (logFilters.dateRange !== 'all') {
        const logDate = new Date(log.timestamp);
        const now = new Date();
        const daysDiff = Math.floor((now.getTime() - logDate.getTime()) / (1000 * 60 * 60 * 24));
        if (logFilters.dateRange === '24h' && daysDiff > 1) return false;
        if (logFilters.dateRange === '7days' && daysDiff > 7) return false;
        if (logFilters.dateRange === '30days' && daysDiff > 30) return false;
      }
      return true;
    });
    const totalPages = Math.ceil(filteredLogs.length / logsPerPage);
    const paginatedLogs = filteredLogs.slice((currentLogPage - 1) * logsPerPage, currentLogPage * logsPerPage);
    const getRelativeTime = (timestamp: string) => {
      const now = new Date();
      const then = new Date(timestamp);
      const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);
      if (seconds < 60) return `${seconds}s ago`;
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      return `${days}d ago`;
    };
    return <div className="space-y-6" data-id="element-11345">
        <div data-id="element-11346">
          <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-11347">
            Activity Logs
          </h2>
          <p className="text-sm text-gray-600" data-id="element-11348">
            View and track all user activities and system events
          </p>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11349">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-11350">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-11351">
              <SearchIcon size={18} className="text-gray-600" data-id="element-11352" />
              Filters
            </h3>
          </div>
          <div className="p-6 space-y-4" data-id="element-11353">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-11354">
              <div data-id="element-11355">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-11356">
                  Search
                </label>
                <input type="text" placeholder="Search logs..." className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" value={logFilters.search} onChange={e => setLogFilters({
                ...logFilters,
                search: e.target.value
              })} data-id="element-11357" />
              </div>
              <div data-id="element-11358">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-11359">
                  Date Range
                </label>
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" value={logFilters.dateRange} onChange={e => setLogFilters({
                ...logFilters,
                dateRange: e.target.value
              })} data-id="element-11360">
                  <option value="all" data-id="element-11361">All Time</option>
                  <option value="24h" data-id="element-11362">Last 24 Hours</option>
                  <option value="7days" data-id="element-11363">Last 7 Days</option>
                  <option value="30days" data-id="element-11364">Last 30 Days</option>
                </select>
              </div>
              <div data-id="element-11365">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-11366">
                  User
                </label>
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" value={logFilters.user} onChange={e => setLogFilters({
                ...logFilters,
                user: e.target.value
              })} data-id="element-11367">
                  <option value="all" data-id="element-11368">All Users</option>
                  {users.map(u => <option key={u.id} value={u.id} data-id="element-11369">
                      {u.name}
                    </option>)}
                </select>
              </div>
              <div data-id="element-11370">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-11371">
                  Section
                </label>
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" value={logFilters.section} onChange={e => setLogFilters({
                ...logFilters,
                section: e.target.value
              })} data-id="element-11372">
                  <option value="all" data-id="element-11373">All Sections</option>
                  <option value="Payments" data-id="element-11374">Payments</option>
                  <option value="Customers" data-id="element-11375">Customers</option>
                  <option value="Invoices" data-id="element-11376">Invoices</option>
                  <option value="Bills" data-id="element-11377">Bills</option>
                  <option value="Refunds" data-id="element-11378">Refunds</option>
                  <option value="Users" data-id="element-11379">Users</option>
                  <option value="Settings" data-id="element-11380">Settings</option>
                  <option value="Security" data-id="element-11381">Security</option>
                </select>
              </div>
              <div data-id="element-11382">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-11383">
                  Per Page
                </label>
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" value={logsPerPage} onChange={e => {
                setLogsPerPage(Number(e.target.value));
                setCurrentLogPage(1);
              }} data-id="element-11384">
                  <option value="25" data-id="element-11385">25</option>
                  <option value="50" data-id="element-11386">50</option>
                  <option value="100" data-id="element-11387">100</option>
                </select>
              </div>
            </div>
            <div className="flex items-center justify-between pt-2" data-id="element-11388">
              <p className="text-sm text-gray-600" data-id="element-11389">
                Showing {paginatedLogs.length} of {filteredLogs.length} logs
              </p>
            </div>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11390">
          <div className="overflow-x-auto" data-id="element-11391">
            <table className="w-full" data-id="element-11392">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-11393">
                <tr data-id="element-11394">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11395">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11396">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11397">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11398">
                    Section
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11399">
                    Object ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11400">
                    IP Address
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-11401">
                {paginatedLogs.map(log => <tr key={log.id} className="hover:bg-gray-50 transition-colors" data-id="element-11402">
                    <td className="px-6 py-4 text-sm text-gray-900" title={new Date(log.timestamp).toLocaleString()} data-id="element-11403">
                      {getRelativeTime(log.timestamp)}
                    </td>
                    <td className="px-6 py-4" data-id="element-11404">
                      <div className="flex items-center gap-2" data-id="element-11405">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-semibold" data-id="element-11406">
                          {log.user.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div data-id="element-11407">
                          <p className="text-sm font-medium text-gray-900" data-id="element-11408">
                            {log.user.name}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-11409">
                            {log.user.role}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-11410">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-11411">
                        {log.action}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600" data-id="element-11412">
                      {log.section}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-mono" data-id="element-11413">
                      {log.objectId}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-mono" data-id="element-11414">
                      {log.ipAddress}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50" data-id="element-11415">
              <button onClick={() => setCurrentLogPage(Math.max(1, currentLogPage - 1))} disabled={currentLogPage === 1} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-11416">
                Previous
              </button>
              <span className="text-sm text-gray-600" data-id="element-11417">
                Page {currentLogPage} of {totalPages}
              </span>
              <button onClick={() => setCurrentLogPage(Math.min(totalPages, currentLogPage + 1))} disabled={currentLogPage === totalPages} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-11418">
                Next
              </button>
            </div>}
        </div>
      </div>;
  };
  const renderRequests = () => {
    const getStatusColor = (status: string) => {
      switch (status) {
        case 'Closed':
          return 'bg-gray-100 text-gray-800';
        case 'Pending':
          return 'bg-amber-100 text-amber-800';
        case 'In Progress':
          return 'bg-blue-100 text-blue-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    };
    const getRelativeTime = (timestamp: string) => {
      const now = new Date();
      const then = new Date(timestamp);
      const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);
      if (seconds < 60) return `${seconds}s ago`;
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      return `${days}d ago`;
    };
    return <div className="space-y-6" data-id="element-11419">
        <div data-id="element-11420">
          <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-11421">
            Merchant Requests
          </h2>
          <p className="text-sm text-gray-600" data-id="element-11422">
            View and track requests for payment method activations and custom
            user roles
          </p>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11423">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-11424">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-11425">
              <ClipboardListIcon size={18} className="text-gray-600" data-id="element-11426" />
              All Requests ({merchantRequests.length})
            </h3>
          </div>
          <div className="overflow-x-auto" data-id="element-11427">
            <table className="w-full" data-id="element-11428">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-11429">
                <tr data-id="element-11430">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11431">
                    Request ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11432">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11433">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11434">
                    Requested By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11435">
                    Requested At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11436">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-11437">
                {merchantRequests.map(request => <tr key={request.id} className="hover:bg-gray-50 transition-colors" data-id="element-11438">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 font-mono" data-id="element-11439">
                      {request.id}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900" data-id="element-11440">
                      {request.type}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs" data-id="element-11441">
                      {request.description}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900" data-id="element-11442">
                      {request.requestedBy}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600" title={new Date(request.requestedAt).toLocaleString()} data-id="element-11443">
                      {getRelativeTime(request.requestedAt)}
                    </td>
                    <td className="px-6 py-4" data-id="element-11444">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`} data-id="element-11445">
                        {request.status}
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-11446">
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-11447">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-11448">
              Total Requests
            </p>
            <p className="text-2xl font-bold text-gray-900" data-id="element-11449">
              {merchantRequests.length}
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-11450">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-11451">
              Pending
            </p>
            <p className="text-2xl font-bold text-amber-600" data-id="element-11452">
              {merchantRequests.filter(r => r.status === 'Pending').length}
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-11453">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-11454">
              Closed
            </p>
            <p className="text-2xl font-bold text-gray-600" data-id="element-11455">
              {merchantRequests.filter(r => r.status === 'Closed').length}
            </p>
          </div>
        </div>
      </div>;
  };
  const renderActiveTab = () => {
    switch (activeTab) {
      case 'company':
        return renderCompanySettings();
      case 'branding':
        return renderBrandingSettings();
      case 'general':
        return renderGeneralSettings();
      case 'receipt':
        return renderReceiptSettings();
      case 'payment-providers':
        return renderPaymentProviders();
      case 'users':
        return renderUsers();
      case 'payout':
        return renderPayoutSettings();
      case 'fields':
        return renderFields();
      case 'notifications':
        return renderNotifications();
      case 'security':
        return renderSecurity();
      case 'subscription-plan':
        return renderSubscriptionPlanSettings();
      default:
        return null;
    }
  };
  return <div className="max-w-7xl mx-auto py-4 sm:py-8 px-4" data-id="element-11456">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8" data-id="element-11457">
        Settings
      </h1>
      <div className="flex flex-col lg:flex-row gap-4 sm:gap-6" data-id="element-11458">
        <nav className="lg:w-64 flex-shrink-0" data-id="element-11459">
          <div className="lg:hidden mb-4" data-id="element-11460">
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide" data-id="element-11461">
              {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${isActive ? 'bg-[var(--brand-primary)] text-[#1A1A1A]' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}`} data-id="element-11462">
                    <Icon size={16} data-id="element-11463" />
                    <span data-id="element-11464">{tab.label}</span>
                  </button>;
            })}
            </div>
          </div>
          <div className="hidden lg:block" data-id="element-11465">
            <div className="space-y-6" data-id="element-11466">
              {categories.map(category => <div key={category.id} data-id="element-11467">
                  <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-11468">
                    {category.label}
                  </h3>
                  <ul className="space-y-1" data-id="element-11469">
                    {tabs.filter(tab => tab.category === category.id).map(tab => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return <li key={tab.id} data-id="element-11470">
                            <button onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive ? 'bg-[var(--brand-primary)] text-[#1A1A1A]' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-11471">
                              <Icon size={16} data-id="element-11472" />
                              <span data-id="element-11473">{tab.label}</span>
                            </button>
                          </li>;
                })}
                  </ul>
                </div>)}
            </div>
          </div>
        </nav>
        <div className="flex-1 bg-white border border-gray-200 rounded-lg p-4 sm:p-6 overflow-x-auto" data-id="element-11474">
          {renderActiveTab()}
        </div>
      </div>
    </div>;
}